import { useCallback } from 'react';
import { useErrorContext } from '@/contexts/useErrorContext';
import { ApiError } from '@/lib/api';
import type { ToastOptions } from '@/types/errors';

/**
 * Hook for handling errors in components.
 * Provides convenient functions for showing errors and success toasts.
 *
 * @param context Optional context string to identify where errors originate (e.g., "Team: Backend")
 *
 * @example
 * ```tsx
 * function TeamForm({ teamName }: { teamName: string }) {
 *   const { handleError, handleSuccess } = useErrorHandler(`Team: ${teamName}`);
 *
 *   const saveTeam = async () => {
 *     try {
 *       await updateTeam(teamId, data);
 *       handleSuccess('Team saved successfully');
 *     } catch (err) {
 *       handleError(err);
 *     }
 *   };
 * }
 * ```
 */
export function useErrorHandler(context?: string) {
  const { showError, showToast } = useErrorContext();

  /**
   * Handle an error and display it as a toast.
   * Automatically extracts details from ApiError instances.
   */
  const handleError = useCallback(
    (error: unknown, options?: Omit<ToastOptions, 'context'>) => {
      if (error instanceof ApiError) {
        showError(error.message, {
          context,
          code: error.code,
          traceId: error.traceId,
          details: error.details,
          ...options,
        });
      } else if (error instanceof Error) {
        showError(error.message, { context, ...options });
      } else if (typeof error === 'string') {
        showError(error, { context, ...options });
      } else {
        showError('An unexpected error occurred', { context, ...options });
      }
    },
    [showError, context],
  );

  /**
   * Show a success toast message.
   */
  const handleSuccess = useCallback(
    (message: string, options?: Omit<ToastOptions, 'severity' | 'context'>) => {
      showToast(message, {
        severity: 'info',
        autoDismiss: true,
        context,
        ...options,
      });
    },
    [showToast, context],
  );

  /**
   * Show a warning toast message.
   */
  const handleWarning = useCallback(
    (message: string, options?: Omit<ToastOptions, 'severity' | 'context'>) => {
      showToast(message, {
        severity: 'warning',
        autoDismiss: true,
        context,
        ...options,
      });
    },
    [showToast, context],
  );

  return {
    handleError,
    handleSuccess,
    handleWarning,
  };
}
